/**
 * A user changes their own sign-in email (board PB-63, plan
 * docs/plan/implementation/email-change.md).
 *
 * Only accounts with a password (the `password` provider): a Google or Apple
 * account's email belongs to that provider. Firebase emails a confirmation
 * link to the new address and changes the sign-in email only when it is
 * clicked; `syncAccountEmail` (Cloud Function) then copies the confirmed
 * address onto the user's record, which users can't write themselves.
 */

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  type User,
} from 'firebase/auth'
import { auth } from '../firebase'
import { functionUrl } from './functionsUrl'

/** Which sign-in method owns this account's email. */
export function emailOwner(
  user: User | null
): 'password' | 'google.com' | 'apple.com' | 'other' {
  const ids = user?.providerData.map(p => p.providerId) ?? []
  if (ids.includes('password')) return 'password'
  if (ids.includes('google.com')) return 'google.com'
  if (ids.includes('apple.com')) return 'apple.com'
  return 'other'
}

/**
 * Re-checks the password, then sends the confirmation link to `newEmail`.
 * Nothing changes until the link is clicked.
 */
export async function requestEmailChange(
  currentPassword: string,
  newEmail: string
): Promise<void> {
  const user = auth.currentUser
  if (!user?.email) throw new Error('Not signed in')
  // With two-step sign-in on, this throws auth/multi-factor-auth-required;
  // the dialog asks for the code, then calls sendEmailChangeLink (PB-62)
  await reauthenticateWithCredential(
    user,
    EmailAuthProvider.credential(user.email, currentPassword)
  )
  await sendEmailChangeLink(newEmail)
}

/** The server's error names → the Firebase codes the pages already handle. */
const ERROR_CODES: Record<string, string> = {
  'requires-recent-login': 'auth/requires-recent-login',
  'email-in-use': 'auth/email-already-in-use',
  'invalid-email': 'auth/invalid-email',
  'too-many': 'auth/too-many-requests',
}

/**
 * Asks the server to send one of the sign-in emails (PB-84): our wording and
 * template, from noreply@mntnd.app, instead of Firebase's own email.
 */
export async function requestAccountEmail(
  fn: 'accountEmail' | 'passwordReset',
  body: Record<string, string>
): Promise<void> {
  const user = auth.currentUser
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  // Fresh token: after re-checking the password its auth_time is new
  if (fn === 'accountEmail' && user)
    headers.Authorization = `Bearer ${await user.getIdToken(true)}`
  const res = await fetch(functionUrl(fn), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const { error } = (await res.json().catch(() => ({}))) as {
      error?: string
    }
    throw Object.assign(new Error(error || `${fn} ${res.status}`), {
      code: (error && ERROR_CODES[error]) || error || `http-${res.status}`,
    })
  }
}

/** Sends the confirm-email link to the signed-in user's own address. */
export function sendConfirmEmail(): Promise<void> {
  return requestAccountEmail('accountEmail', { action: 'verify' })
}

/** Sends the confirmation link to the new address; needs a recent sign-in. */
export function sendEmailChangeLink(newEmail: string): Promise<void> {
  return requestAccountEmail('accountEmail', {
    action: 'changeEmail',
    newEmail,
  })
}

/** Copies the confirmed sign-in email onto the user's record (server side). */
export async function syncAccountEmail(): Promise<boolean> {
  const user = auth.currentUser
  if (!user) return false
  const res = await fetch(functionUrl('syncAccountEmail'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await user.getIdToken()}`,
    },
    body: '{}',
  })
  if (!res.ok) throw new Error(`syncAccountEmail ${res.status}`)
  const data = (await res.json()) as { changed?: boolean }
  return data.changed === true
}

/**
 * "Use {google email}" (PB-83, D107 A): the server makes the account
 * Google-only (sign-in email = the Google address, password removed) and
 * updates the record. Throws an error
 * whose `code` is `auth/requires-recent-login` when the sign-in is stale, so
 * the confirm-it's-you dialog can take over.
 */
export async function switchToGoogleEmail(): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('Not signed in')
  const res = await fetch(functionUrl('syncAccountEmail'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Fresh token: after "confirm it's you" its auth_time is new
      Authorization: `Bearer ${await user.getIdToken(true)}`,
    },
    body: JSON.stringify({ useProvider: 'google.com' }),
  })
  if (!res.ok) {
    const { error } = (await res.json().catch(() => ({}))) as {
      error?: string
    }
    throw Object.assign(new Error(error || `syncAccountEmail ${res.status}`), {
      code:
        error === 'requires-recent-login'
          ? 'auth/requires-recent-login'
          : error === 'email-in-use'
            ? 'auth/email-already-in-use'
            : error,
    })
  }
  await user.reload()
  await user.getIdToken(true)
  // Only report success once the sign-in email really is the Google one:
  // an older function answered OK without changing anything (2026-10-30).
  const google = user.providerData.find(p => p.providerId === 'google.com')
  if (
    !google?.email ||
    user.email?.toLowerCase() !== google.email.toLowerCase()
  )
    throw Object.assign(new Error('Sign-in email did not change'), {
      code: 'email-not-changed',
    })
}
